import React from 'react';
import { devLog } from '../../../../../utils/devLog';
import { CREW_POSITIONS } from '../../../../../config/crewRequirements';
import { useCrewConfig } from '../../../../../hooks/domain/crew/useCrewConfig';
import Dropdown from '../../../../common/Dropdown';
import { Calendar, Phone, Mail, Briefcase } from 'lucide-react';

const PersonalInfoSection = ({ formData, errors, currentVessel, handleInputChange }) => {
  const vesselAreaCategory = currentVessel?.category || 3;
  const vesselEnginePower = currentVessel?.enginePower || 'under1500';
  const vesselLength = currentVessel?.length || 0;

  // Get enabled positions from crew config (respects vessel-specific settings)
  const { enabledPositions } = useCrewConfig();

  // Use enabled positions if available, otherwise fall back to all positions
  const availablePositions = enabledPositions?.length > 0 ? enabledPositions : CREW_POSITIONS;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-900">Personal Information</h3>
        <p className="mt-1 text-sm text-gray-600">Basic details about the crew member</p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-6">
        <div className="sm:col-span-3">
          <label htmlFor="name" className="block text-sm font-medium text-gray-900">
            Full Name <span className="text-red-500">*</span>
          </label>
          <div className="mt-2">
            <input
              type="text"
              id="name"
              value={formData.name || formData.full_name || ''}  // Handle both fields for compatibility
              onChange={(e) => {
                if (process.env.NODE_ENV !== 'production') {
                  devLog('[PersonalInfo] Name input:', e.target.value);
                }
                handleInputChange('name', e.target.value);
              }}
              required
              className={`block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 ${
                errors.name || errors.full_name ? 'outline-red-500' : 'outline-gray-300'
              } placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm`}
            />
            {(errors.name || errors.full_name) && (
              <p className="mt-1 text-sm text-red-600">{errors.name || errors.full_name}</p>
            )}
          </div>
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="identification" className="block text-sm font-medium text-gray-900">
            ID/Passport Number
          </label>
          <div className="mt-2">
            <input
              type="text"
              id="identification"
              value={formData.identification}
              onChange={(e) => handleInputChange('identification', e.target.value)}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            />
          </div>
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="position" className="block text-sm font-medium text-gray-900">
            <Briefcase className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
            Position <span className="text-red-500">*</span>
          </label>
          <div className="mt-2">
            <Dropdown
              value={formData.position}
              onChange={(value) => handleInputChange('position', value)}
              width="w-full"
              options={[
                { value: '', label: 'Select position' },
                ...availablePositions.map((pos) => ({ value: pos, label: pos }))
              ]}
            />
            {errors.position && (
              <p className="mt-1 text-sm text-red-600">{errors.position}</p>
            )}
          </div>
        </div>

        {/* Show vessel context info */}
        {currentVessel && (
          <div className="sm:col-span-6">
            <div className="rounded-md bg-blue-50 p-3">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Vessel Configuration:</span> 
                {' '}Category {vesselAreaCategory} • 
                {vesselEnginePower && ` ${vesselEnginePower} engine power`}
                {vesselLength && ` • ${vesselLength}m length`}
              </p>
            </div>
          </div>
        )}

        <div className="sm:col-span-3">
          <label htmlFor="phone" className="block text-sm font-medium text-gray-900">
            <Phone className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
            Mobile Phone
          </label>
          <div className="mt-2">
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            />
          </div>
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="email" className="block text-sm font-medium text-gray-900">
            <Mail className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
            Email
          </label>
          <div className="mt-2">
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            />
          </div>
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="start_date" className="block text-sm font-medium text-gray-900">
            <Calendar className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
            Start Date
          </label>
          <div className="mt-2">
            <input
              type="date"
              id="start_date"
              value={formData.start_date}
              onChange={(e) => handleInputChange('start_date', e.target.value)}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoSection;